// Branded error panel — replaces raw `<p style={{color: danger}}>{err.message}</p>`
// on data pages where a fetch failure used to leak "Failed to fetch" to the
// user. Pairs with humanizeError() in lib/humanizeError.
//
// Round-4 #2: 20 of 20 personas surfaced "Failed to fetch" as a trust-killer.
// This is the visual treatment + Retry button + email-support fallback.
//
// Usage:
//   {error ? <ApiError error={error} onRetry={() => reload()} /> : null}

import { humanizeError } from '../../lib/humanizeError';

interface ApiErrorProps {
  error: unknown;
  /** Optional callback. When present, renders a "Try again" button. */
  onRetry?: () => void;
  /** Optional one-line context above the error message. */
  title?: string;
}

export function ApiError({ error, onRetry, title }: ApiErrorProps): JSX.Element {
  const message = humanizeError(error);
  return (
    <section
      role="alert"
      aria-live="polite"
      style={{
        padding: 'var(--space-md)',
        margin: '0 0 var(--space-md)',
        background: 'var(--color-warning-surface, #fef3c7)',
        border: '1px solid var(--color-warning-border, #fbbf24)',
        borderRadius: 'var(--radius-md)',
        display: 'grid',
        gap: 12,
      }}
    >
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--color-text-primary)' }}>
        {title ?? 'We hit a snag'}
      </h3>
      <p style={{ margin: 0, fontSize: 16, lineHeight: 1.5, color: 'var(--color-text-primary)' }}>
        {message}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            style={{
              minHeight: 48,
              padding: '0 18px',
              background: 'var(--color-primary)',
              color: '#fff',
              border: 0,
              borderRadius: 10,
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        ) : null}
        <a
          href="mailto:support@otchealthmart.com?subject=MedReview%20error"
          style={{
            minHeight: 48,
            padding: '12px 18px',
            background: 'transparent',
            color: 'var(--color-text-primary)',
            border: '1.5px solid var(--color-border)',
            borderRadius: 10,
            fontSize: 16,
            fontWeight: 600,
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          Email support
        </a>
      </div>
    </section>
  );
}
